import CustomModal from '@components/CustomModal';
import AddPolicyForm from '@components/drawers/AddPolicyForm';
import CustomDrawer from '@components/drawers/CustomDrawer';
import ConfirmLeaveModal from '@components/modals/ConfirmLeaveModal';
import DeletePolicyModal from '@components/modals/DeletePolicyModal';
import WolfTooltip from '@components/ui/WolfTooltip';
import { graphQlClient } from '@config/graphqlClient';
import PoliciesGraphQL from '@graphql/policy.queries';
import ProcessesGraphQL from '@graphql/process.queries';
import { useLoader } from '@hooks/useLoader';
import { useTranslation } from '@hooks/useTranslation';
import { Area } from '@models/area.model';
import { DrawerType, SnackType } from '@models/common.model';
import { createOrUpdatePolicyRequest, deletePoliciesRequest, getPolicyByIdRequest, Policy } from '@models/policy.model';
import { getProcessesWithAreaServiceRequest, Process } from '@models/process.model';
import { Service } from '@models/service.model';
import { BusinessOutlined, DescriptionOutlined, EditOutlined, FolderOutlined } from '@mui/icons-material';
import { Box, Card, CardContent, Chip, Container, Divider, IconButton } from '@mui/material';
import Typography from '@mui/material/Typography';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import sanitizeHTML from '@utils/sanitize-html';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

const PolicyDescription: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [policyData, setPolicyData] = useState<Policy>();
  const [processes, setProcesses] = useState<Process[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedPolicyForDeletion, setSelectedPolicyForDeletion] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmLeaveModal, setShowConfirmLeaveModal] = useState(false);

  const localePolicies = useTranslation('policies');
  const localeCommon = useTranslation('common');
  const localeActionMessages = useTranslation('actionMessages');

  const { setLoading } = useLoader();
  const { policyId } = useParams<{ policyId: string }>(); // TODO BRIAN types
  // Get params & get services by areaId or all services if no ID
  useEffect(() => {
    getPolicyDetails(policyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyId]);

  const getPolicyDetails = async (policyId: string) => {
    try {
      setLoading(true);
      const data: getPolicyByIdRequest = await graphQlClient.request(PoliciesGraphQL.queries.getPolicyById, {
        id: policyId,
      });
      setPolicyData(data?.getPolicyById);
      getProcesses(data?.getPolicyById);
    } catch (e: any) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  //TODO This call could be made to a new separate endpoint
  const getProcesses = async (policy: Policy) => {
    try {
      const data: getProcessesWithAreaServiceRequest = await graphQlClient.request(
        ProcessesGraphQL.queries.getProcessesWithAreaService,
      );

      const relatedProcesses = policy?.processes?.map((item: { processId: string }) => item.processId);

      const newProcesses: Process[] = data.getProcessesWithAreaService.filter((process: Process) =>
        relatedProcesses?.includes(process.processId),
      );
      const newServices: Service[] = [];
      newProcesses.forEach((process: Process) => {
        if (process.service && !newServices.some((service) => process?.service?.serviceId === service.serviceId)) {
          newServices.push(process.service);
        }
      });
      const newAreas: Area[] = [];
      newServices.forEach((service: Service) => {
        if (service.area && !newAreas.some((area) => service?.area?.areaId === area.areaId)) {
          newAreas.push(service.area);
        }
      });

      setAreas(newAreas);
      setServices(newServices);
      setProcesses(newProcesses);
    } catch (e: any) {
      console.log(e);
    }
  };

  // DRAWER HANDLERS *********************
  const drawerEditOpen = () => {
    setShowDrawer(true);
  };

  const drawerClose = (): void => {
    if (hasUnsavedChanges) {
      setShowConfirmLeaveModal(true);
    } else {
      setShowDrawer(false);
    }
  };

  const closeConfirmLeaveModal = () => {
    setShowConfirmLeaveModal(false);
  };

  const closeAndConfirmLeaveModal = () => {
    setShowConfirmLeaveModal(false);
    setShowDrawer(false);
  };

  const handlePolicyFormChanges = (changes: boolean) => {
    setHasUnsavedChanges(changes);
  };

  const handleDrawerSave = async (policy: Policy) => {
    setSaving(true);

    try {
      const data: createOrUpdatePolicyRequest = await graphQlClient.request(
        PoliciesGraphQL.mutations.updatePolicy,
        {
          policy: {
            policyId: policy.policyId,
            name: policy.name,
            content: policy.content,
            category: policy.category,
            description: policy.description,
          },
        },
      );
      if (data?.updatePolicy?.policyId) {
        await getPolicyDetails(policyId);
        dispatch(
          appendActionMessage({
            message: localeActionMessages['policyUpdatedSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );
        setShowDrawer(false);
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const deletePolicy = async (id: string) => {
    setSelectedPolicyForDeletion(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteModal(false);
    try {
      const data: deletePoliciesRequest = await graphQlClient.request(PoliciesGraphQL.mutations.deletePolicy, {
        id: selectedPolicyForDeletion,
      });
      if (data?.deletePolicy) {
        dispatch(
          appendActionMessage({ message: localeActionMessages['policyDeletedSuccessfully'], type: SnackType.SUCCESS }),
        );
        // Close modal and navigate back to policies list after deletion
        setHasUnsavedChanges(false);
        setShowDrawer(false);
        setSelectedPolicyForDeletion('');
        history.push('/policies');
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
      setShowDeleteModal(false);
      setSelectedPolicyForDeletion('');
    }
  };

  const cancelDeletePolicy = () => {
    setShowDeleteModal(false);
    setSelectedPolicyForDeletion('');
  };

  return (
    <Container maxWidth={false} sx={{ maxWidth: '80%' }} className="py-6">
      {/* Header Section */}
      <Box className="flex justify-between items-start mb-6">
        <Box className="flex-1">
          <Typography variant="h1" className="font-bold mb-2">
            {policyData?.name}
          </Typography>
          {policyData?.category && (
            <Chip
              label={policyData.category}
              color="primary"
              variant="outlined"
              className="mt-2"
            />
          )}
        </Box>
        <WolfTooltip title={localeCommon['clickEdit']}>
          <IconButton
            aria-label="edit"
            onClick={drawerEditOpen}
            className="ml-4">
            <EditOutlined />
          </IconButton>
        </WolfTooltip>
      </Box>

      {/* Brief Description Section */}
      {policyData?.description && (
        <Card className="mb-6 shadow-md">
          <CardContent className="p-6">
            <Typography variant="h6" className="font-semibold mb-3">
              {localeCommon['description']}
            </Typography>
            <Typography variant="body1" className="text-gray-700">
              {policyData.description}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Content Section */}
      <Card className="mb-6 shadow-md">
        <CardContent className="p-6">
          <div
            className="prose max-w-none"
            dangerouslySetInnerHTML={sanitizeHTML(policyData?.content)}
          />
        </CardContent>
      </Card>

      {/* Used In Section */}
      {areas.length > 0 && (
        <Card className="shadow-md">
          <CardContent className="p-6">
            <Typography variant="h5" className="font-bold mb-4 flex items-center gap-2">
              <FolderOutlined />
              {localePolicies['usedIn']}
            </Typography>

            <Divider className="mb-4" />

            <Box className="space-y-4">
              {areas.map((area: Area) => (
                <Box key={area.areaId} className="mb-6">
                  {/* Area */}
                  <Box className="flex items-center gap-2 mb-3">
                    <BusinessOutlined className="text-primary" />
                    <Typography variant="h6" className="font-semibold">
                      {area.name}
                    </Typography>
                  </Box>

                  {/* Services */}
                  <Box className="ml-6 space-y-3">
                    {services
                      .filter((service) => service.area?.areaId === area.areaId)
                      .map((service: Service) => (
                        <Box key={service.serviceId}>
                          <Typography variant="body1" className="font-medium text-gray-700 mb-2">
                            {service.name}
                          </Typography>

                          {/* Processes */}
                          <Box className="ml-6 space-y-1">
                            {processes
                              .filter((process) => process.service?.serviceId === service.serviceId)
                              .map((process: Process) => (
                                <Box
                                  key={process.processId}
                                  className="flex items-center gap-2 py-1"
                                >
                                  <DescriptionOutlined
                                    fontSize="small"
                                    className="text-gray-500"
                                  />
                                  <Typography variant="body2" className="text-gray-600">
                                    {process.name}
                                  </Typography>
                                </Box>
                              ))}
                          </Box>
                        </Box>
                      ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Edit Drawer */}
      <CustomDrawer
        title={localeCommon['edit'] + ' ' + localeCommon['policy']}
        isOpen={showDrawer}
        onClose={drawerClose}>
        <AddPolicyForm
          onClose={drawerClose}
          handleSave={handleDrawerSave}
          handleDelete={deletePolicy}
          onChanges={handlePolicyFormChanges}
          drawerType={DrawerType.EDIT}
          initialData={policyData}
          saving={saving}
        />
      </CustomDrawer>

      {/* Delete Confirmation Modal */}
      <CustomModal
        showButtons={true}
        isOpen={showDeleteModal}
        onClose={cancelDeletePolicy}
        onConfirm={handleConfirmDelete}
        maxWidth="600px"
        title={localePolicies['confirmDeleteTitle']}
        customConfirmColor="error"
        customConfirmText={localeCommon['delete']}>
        <DeletePolicyModal open={showDeleteModal} policyId={selectedPolicyForDeletion} />
      </CustomModal>

      {/* Confirm Leave Modal */}
      {showConfirmLeaveModal && (
        <CustomModal
          isOpen={!!showConfirmLeaveModal}
          title={localeCommon['confirmLeave']}
          customConfirmColor="error"
          customCancelText={localeCommon['backToEditing']}
          customConfirmText={localeCommon['discardChanges']}
          maxWidth="600px"
          showButtons={true}
          onClose={closeConfirmLeaveModal}
          onConfirm={closeAndConfirmLeaveModal}>
          <ConfirmLeaveModal />
        </CustomModal>
      )}
    </Container>
  );
};

export default PolicyDescription;
